import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Folder, Rocket, Brain, Settings, Activity,
} from 'lucide-react';
import RepoPanel from './RepoPanel';
import MemoryPanel from './MemoryPanel';
import ConfigPanel from './ConfigPanel';
import ObservabilityPanel from './ObservabilityPanel';
import DeployPanel from './DeployPanel';

export type DrawerSection =
  | 'files' | 'deploy' | 'memory' | 'config' | 'observability';

interface Props {
  open: boolean;
  section: DrawerSection;
  onSectionChange: (s: DrawerSection) => void;
  onClose: () => void;
  workspace: any;
  onWorkspaceUpdate: () => void;
}

const SECTIONS: {
  key: DrawerSection; label: string; icon: any; testid: string;
}[] = [
  { key: 'files',         label: 'Archivos',       icon: Folder,   testid: 'workspace-drawer-files' },
  { key: 'deploy',        label: 'Deploy',         icon: Rocket,   testid: 'workspace-drawer-deploy' },
  { key: 'memory',        label: 'Memoria',        icon: Brain,    testid: 'workspace-drawer-memory' },
  { key: 'observability', label: 'Observabilidad', icon: Activity, testid: 'workspace-drawer-observability' },
  { key: 'config',        label: 'Configuración',  icon: Settings, testid: 'workspace-drawer-config' },
];

/**
 * WorkspaceDrawer — right-side slide-over that holds every non-chat
 * surface (files, deploy, memory, config, observability). Opened from
 * a single "Menu" button so the workspace stays purely conversational.
 */
export const WorkspaceDrawer: React.FC<Props> = ({
  open, section, onSectionChange, onClose, workspace, onWorkspaceUpdate,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="workspace-drawer-overlay"
          />

          {/* Panel */}
          <motion.aside
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-[540px] md:w-[640px] lg:w-[720px] nx-glass-drawer shadow-2xl flex flex-col"
            data-testid="workspace-drawer"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-white/[0.06] shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Panel
                </div>
                <div className="text-sm text-zinc-100 font-medium">
                  {SECTIONS.find((s) => s.key === section)?.label}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="workspace-drawer-close"
                className="w-9 h-9 grid place-items-center rounded-full text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.06] transition"
                aria-label="Cerrar panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Section tabs */}
            <div className="px-3 py-3 flex gap-1 overflow-x-auto border-b border-white/[0.04] shrink-0 tabs-scroll">
              {SECTIONS.map(({ key, label, icon: Icon, testid }) => {
                const active = section === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={testid}
                    onClick={() => onSectionChange(key)}
                    className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition"
                    style={{
                      background: active ? 'hsl(var(--accent))' : 'transparent',
                      color: active ? '#0a0f10' : 'hsl(var(--text-muted))',
                      border: `1px solid ${active ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
                      boxShadow: active ? '0 4px 14px hsl(var(--accent) / 0.3)' : 'none',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto"
              data-testid={`workspace-drawer-content-${section}`}
            >
              {section === 'files' && (
                <div className="p-4">
                  <RepoPanel workspaceId={workspace.id} />
                </div>
              )}
              {section === 'deploy' && (
                <div className="p-4">
                  <DeployPanel workspaceId={workspace.id} />
                </div>
              )}
              {section === 'memory' && (
                <div className="p-4">
                  <MemoryPanel workspaceId={workspace.id} />
                </div>
              )}
              {section === 'config' && (
                <div className="p-4">
                  <ConfigPanel workspace={workspace} onUpdate={onWorkspaceUpdate} />
                </div>
              )}
              {section === 'observability' && (
                <div className="h-full min-h-[500px]">
                  <ObservabilityPanel workspaceId={workspace.id} defaultWindow="24h" />
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default WorkspaceDrawer;
